import { useCallback, useEffect, useMemo, useState } from 'react'
import { DoxRequest } from '@grammarly/http-clients-dox'
import { TextChecker } from '@grammarly/website-components'

import {
  useApiService,
  useBrowserService,
  useExperimentService,
  useUserService,
  useFreeLimitToTrialExperiment,
} from '../../../../lib/hooks'
import { EventProps } from '../../../../lib/tracking/gnar'
import { logError, LogErrorMetrics } from '../../../../lib/tracking/felog'
import { feMetrics } from '@lib/tracking/metrics'
import * as CmsMetrics from '@lib/tracking/metrics/metrics'
import { LocalStorageKey, setLocalStorageItem } from '../../../../lib/utils/local-storage'
import { transformToString, transformToObject } from '../../../../lib/utils/error'
import { getConfig } from '../../../../config'
import { SignUpGoogleButton } from '../../../../components/item/cta-button/signup-google'
import {
  getExpOptimalUsageLimit,
  isToolsOptimalUsageLimitTestTreatment,
  UrlBasedUsageTracker,
} from '@lib/utils/tool-usage'
import { trackExperimentInfo } from '@lib/tracking/google-tag-manager/events'
import { navHelper } from '@lib/services/navigation'
import { FreeUserTrialModal } from '@components/ui-lib/modal/free-user-trial-modal'
import { DEFAULT_ROOT_ELEMENT_SELECTOR } from '@components/ui-lib/modal'
import { SessionStorageKey } from '@lib/utils/session-storage'

import { isEligibleGoogleOneTap } from '@components/google-one-tap/eligibility'
import GoogleOneTapDefault from '@components/google-one-tap/default'
import styles from './editor-plugin.module.scss'
import { ToolType } from '@components/tool/types'

const baseDomain = getConfig().domains.main
const shBaseDomain = getConfig().domains.superhuman
const maxCountSubmitAnonymous = 1

interface G4DTextCheckerProps {
  isWordCounter: boolean
  placeholder: string
  gnarTrackFn: (trackEventProps: EventProps) => Promise<void>
  autofocus?: boolean
  bottomSectionLabel?: string
  slug?: string
}

export const G4DTextChecker = (props: G4DTextCheckerProps) => {
  const { autofocus, isWordCounter, bottomSectionLabel, placeholder, gnarTrackFn, slug } = props
  const { dox } = useApiService()
  const { currentUser } = useUserService()
  const { isAnonymous } = currentUser
  const { isMobileOrTablet, isChrome } = useBrowserService()
  const {
    peekToolsOptimalUsageLimitAssignment,
    isGoogleCtaSignupWallV2,
    getToolsOptimalUsageLimitAssignment,
    experimentsWithAssignedTreatment,
    getToolsGoogleOneTapAssignment,
  } = useExperimentService()
  const [showToolsGoogleOneTap, setShowToolsGoogleOneTap] = useState(false)

  const peekedToolsOptimalUsageLimitAssignment = peekToolsOptimalUsageLimitAssignment(isAnonymous, slug ?? '')
  const isExpTestTreatment = isToolsOptimalUsageLimitTestTreatment(peekedToolsOptimalUsageLimitAssignment)
  const usageLimit = getExpOptimalUsageLimit(peekedToolsOptimalUsageLimitAssignment, maxCountSubmitAnonymous)

  const { usageTracker: freeTrialUsageTracker, showTrialModal, setShowTrialModal } = useFreeLimitToTrialExperiment()

  const triggerGoogleOneTapOnAcceptSuggestion = useCallback(() => {
    const isEligible =
      isEligibleGoogleOneTap({
        isAnonymous,
        isChrome,
        isGoogleOneTapEnabled: false,
        isSSO: navHelper.currentQueryParams.is_sso === 'true',
        pageSlug: slug ?? '',
      }) && getToolsGoogleOneTapAssignment(isMobileOrTablet) === 'task_complete'
    if (isEligible) {
      setShowToolsGoogleOneTap(true)
    }
  }, [isAnonymous, isChrome, slug, isMobileOrTablet, getToolsGoogleOneTapAssignment])

  const grammarCheckerUsageTracker = useMemo(() => {
    if (isExpTestTreatment) {
      return new UrlBasedUsageTracker(usageLimit, false, undefined, currentUser)
    }
    return freeTrialUsageTracker
  }, [isExpTestTreatment, currentUser, usageLimit, freeTrialUsageTracker])

  useEffect(() => {
    if (isExpTestTreatment && grammarCheckerUsageTracker) {
      grammarCheckerUsageTracker.addEventListener('usage', triggerGoogleOneTapOnAcceptSuggestion)
      return () => {
        grammarCheckerUsageTracker.removeEventListener('usage', triggerGoogleOneTapOnAcceptSuggestion)
      }
    }
  }, [isExpTestTreatment, grammarCheckerUsageTracker, triggerGoogleOneTapOnAcceptSuggestion])

  const onG4DPluginError = useCallback((e: CustomEvent<Error>) => {
    logError(`G4D Editor Error: ${transformToString(e)}`, LogErrorMetrics.G4DEditor, {
      error: transformToObject(e),
    })

    feMetrics
      .getMetric(CmsMetrics.GeneralClientMetrics.ToolApi)
      .getCounter(CmsMetrics.ClientMetricsActions.Failure)
      .setContext({
        toolPage: CmsMetrics.ToolType.GrammarCheck,
      })
      .increment()
  }, [])

  const onInitialChange = useCallback(() => {
    feMetrics
      .getMetric(CmsMetrics.GeneralClientMetrics.ToolUser)
      .getCounter(CmsMetrics.ClientMetricsActions.Input)
      .setContext({
        toolPage: CmsMetrics.ToolType.GrammarCheck,
      })
      .increment()
  }, [])

  const onSaveText = async (text: string) => {
    if (isAnonymous) {
      // this local storage item will be read and removed after navigating to Funnel
      setLocalStorageItem(LocalStorageKey.checkedText, text)

      sessionStorage.setItem(SessionStorageKey.editorContent, text)
    } else {
      const trimmedText = text.trim()
      const doxCreateRequest = DoxRequest.Create.textFull({
        doc: { ops: [{ insert: trimmedText }] },
        first_content: trimmedText.substring(0, 250),
      })
      const doxDocument = await dox.createDocument(doxCreateRequest)
      return doxDocument.id
    }
  }
  const assignmentAndGA4Tracking = (isAnonymous: boolean, slug: string) => {
    const assignment = getToolsOptimalUsageLimitAssignment(isAnonymous, slug)
    trackExperimentInfo(slug, experimentsWithAssignedTreatment)
    return assignment
  }

  useEffect(() => {
    if (sessionStorage.getItem(SessionStorageKey.editorContent) !== null) {
      sessionStorage.removeItem(SessionStorageKey.editorContent)
    }
    sessionStorage.setItem(SessionStorageKey.toolLastUsed, 'grammarChecker' as ToolType)
  }, [])

  return (
    <>
      {showToolsGoogleOneTap && <GoogleOneTapDefault />}
      <TextChecker
        clientName="cms"
        baseDomain={baseDomain}
        shBaseDomain={shBaseDomain}
        gnarTrackFn={gnarTrackFn}
        isAnonymous={isAnonymous}
        isMobileTablet={isMobileOrTablet}
        placeholder={placeholder}
        autofocus={autofocus}
        onSaveText={onSaveText}
        onPluginError={onG4DPluginError}
        onInitialChange={onInitialChange}
        suggestionsBeforeWall={isAnonymous ? maxCountSubmitAnonymous : -1}
        appElementForModal={DEFAULT_ROOT_ELEMENT_SELECTOR}
        assignExperiments={{
          isGoogleCtaSignupWall: isGoogleCtaSignupWallV2,
          getToolsOptimalUsageLimitAssignment: assignmentAndGA4Tracking,
        }}
        slug={slug}
        signUpGoogleButton={
          <SignUpGoogleButton text="Sign up with Google" className={styles.signUpGoogleButton} toQueryParam="editor" />
        }
        isWordCounter={isWordCounter}
        bottomSectionLabel={bottomSectionLabel}
        intoolSignupGoogleButton={
          <SignUpGoogleButton
            text="Sign up with Google"
            className={styles.inToolGoogleSignupButton}
            toQueryParam="editor"
          />
        }
        usageTracker={grammarCheckerUsageTracker}
      />
      <FreeUserTrialModal show={showTrialModal} close={() => setShowTrialModal(false)} />
    </>
  )
}
